/**
 * The document's **Extractions** tab (engine W15, FR-E3, FR-E5, FR-E6, FR-E9,
 * FR-E18): what has been read out of it, newest first, and — where the rule
 * allows — *Read it as…*.
 *
 * **Parsing happens only when asked** (FR-E1, ADR-0013). The tab offers it
 * only by FR-E3's rule: the person may ask (`extraction:can_create`), the
 * organisation's screens offer parsing (`parsing.offered`, FR-E38), the
 * document is live, and **its type has a profile** the organisation has not
 * switched off that reads this kind of file — by `PROFILE_FOR_TYPE` (engine W17,
 * D54), so a credit or debit note is offered `invoice`. A person's request is
 * reviewed in ProDoc (`review: auto`).
 *
 * The request answers at once with its job (FR-E9); the tab follows it —
 * waiting, being read, then read, refused with its reason (and the engine's
 * suggestion, FR-E24) or failed — and lists the extraction when it lands. A
 * version already read is not read twice by accident: the service returns the
 * same job (FR-E5), and reading it again is asked for **with a reason**. An
 * encrypted PDF's password is typed here, sent with that one request, and kept
 * nowhere (FR-E6).
 *
 * **Whose books** (T15.16, FR-E7; the owner, 2026-10-14: the GSTIN is the
 * client's or the company's, passed by the app — ProDoc's own screen takes an
 * optional one): the person may name the party's GSTINs and PAN, sent as the
 * request's `context`. Without them an invoice's side — sale or purchase — is
 * `unknown`, and it goes to a reviewer. ProDoc keeps no "whose books" record,
 * so nothing is filled in for them.
 *
 * **A grid no saved map fits** (engine W32, FR-E49, FR-E55, D130): a reading of a
 * mapped profile (`books_ledger`) refused `mapping_needed` offers *Map the
 * columns* — `<ProDocColumnMapper>` with the refusal's suggestion filled in. Once
 * the map is saved, the same request is asked again under its scope
 * (`options.map_scope`, `org` unless the host says otherwise) and followed as any.
 */
import { Alert, Badge, Button, Dialog, EmptyState, Input, RadioGroup, Skeleton, TONE_TEXT, cn } from '@fiveox/ui'
import { CircleAlert, CircleCheck, LoaderCircle, ScanText } from 'lucide-react'
import { useEffect, useState } from 'react'

import { mediaLabel, when } from './bits'
import { ProDocColumnMapper } from './column-mapper'
import { ExtractionStateBadge, ReconciledBadge, profileOf } from './extraction'
import {
  MAPPING_NEEDED_COPY,
  MAX_CORRECTION_REASON,
  WAITING_INHOUSE,
  jobReasonText,
  jobStatusLabel,
  partyContextOf,
  profileForType,
  reviewReasonLabel,
  waitingInhouseText,
  type ExtractionRecord,
  type MappingNeeded,
  type ParseInput,
  type ParseJob,
  type ParseProfile,
  type ProfileList,
} from './extraction-model'
import { errorText, useLoad } from './hooks'
import { JOB_POLL_MS, useFollowedJob } from './jobs'
import { EXTRACTION, documentTypeLabel, passwordSavedOf, type DocumentTypeOption, type ProDocClient, type ProDocDocument, type Verb } from './prodoc-client'

/** What FR-E3's rule says for this document: what it may be read as, or why not — `null` when there is nothing to offer or say. */
export type ParseOffer = { profile: ParseProfile; fitting: ParseProfile[] } | { reason: string } | null

export function parseOfferFor(doc: Pick<ProDocDocument, 'status' | 'document_type' | 'media_type'>, list: ProfileList | null, canCreate: boolean, types: DocumentTypeOption[] = []): ParseOffer {
  if (!canCreate || !list || doc.status !== 'stored' || list.offered === false) return null
  // D54 (W17): the type's profile by the table — a credit or debit note is offered `invoice`.
  const name = profileForType(doc.document_type)
  const profile = list.items.find((p) => p.name === name && !p.internal)
  if (!profile) return null
  const label = documentTypeLabel(profile.name, types)
  if (!profile.enabled) return { reason: `Reading ${label.toLowerCase()} documents is switched off for this organisation.` }
  if (!profile.media_types.includes(doc.media_type)) {
    return { reason: `ProDoc reads ${label.toLowerCase()} documents from ${[...new Set(profile.media_types.map(mediaLabel))].join(', ') || 'other kinds of file'}, not from a ${mediaLabel(doc.media_type)}.` }
  }
  const fitting = [profile, ...list.items.filter((p) => p !== profile && p.enabled && !p.internal && p.media_types.includes(doc.media_type))]
  return { profile, fitting }
}

export interface ExtractionsPanelProps {
  client: ProDocClient
  doc: ProDocDocument
  can: (resource: string, verb: Verb) => boolean
  types: DocumentTypeOption[]
  /** Open one extraction — the host's route to its screen. Without it, extractions are listed, not links. */
  onOpenExtraction?: (document: ProDocDocument, extraction: ExtractionRecord) => void
  jobPollMs?: number
  /**
   * W32 (FR-E48): the scope a map made here is saved under and the reading asked again
   * with (`options.map_scope`) — `org` unless the host says `client:<id>`.
   */
  mapScope?: string
}

export function ExtractionsPanel({ client, doc, can, types, onOpenExtraction, jobPollMs = JOB_POLL_MS, mapScope = 'org' }: ExtractionsPanelProps) {
  const canCreate = can(EXTRACTION, 'can_create')
  const [revision, setRevision] = useState(0)
  const records = useLoad(() => client.parsing.extractions(doc.id), [client, doc.id, revision])
  const profiles = useLoad(() => (canCreate ? client.parsing.profiles() : Promise.resolve(null)), [client, canCreate])
  const [asking, setAsking] = useState<string | null>(null)
  const [started, setStarted] = useState<ParseJob | null>(null)
  const followed = useFollowedJob(client, started, jobPollMs, () => setRevision((n) => n + 1))
  // W32: the request this tab last made — without its password, which is never kept — asked again once a map is saved.
  const [asked, setAsked] = useState<ParseInput | null>(null)
  const [mapping, setMapping] = useState<ParseJob | null>(null)
  const [reaskError, setReaskError] = useState('')

  const offer = parseOfferFor(doc, profiles.data ?? null, canCreate, types)
  const fitting = offer && 'fitting' in offer ? offer.fitting : []
  const list = records.data ?? []
  const job = followed.job
  const landed = job?.extraction_id ? list.find((r) => r.id === job.extraction_id) : undefined
  // FR-E49: a refusal for want of a map is mapped here when the person may ask and the profile has fields to map.
  const mapProfile = job?.profile ? (profiles.data?.items.find((p) => p.name === job.profile && !p.internal) ?? null) : null
  const mayMap = Boolean(canCreate && job?.status === 'refused' && job.reason === 'mapping_needed' && (mapProfile?.map_fields?.length ?? 0) > 0)

  /** After a save: the same request again, under the map's scope (the contract: a refused job is no repeat). */
  const reask = async (refused: ParseJob) => {
    const base: ParseInput = asked && asked.profile === refused.profile ? asked : { profile: refused.profile ?? '', version_id: refused.version_id || doc.current_version_id, review: 'auto' }
    setReaskError('')
    try {
      const answer = await client.parsing.parse(doc.id, { ...base, password: null, options: { ...(base.options ?? {}), map_scope: mapScope } })
      setStarted(answer.job)
      setRevision((n) => n + 1)
    } catch (e) {
      setReaskError(errorText(e, 'ProDoc was not asked to read it again.'))
    }
  }

  return (
    <div className="space-y-4">
      {offer && 'fitting' in offer && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-muted-foreground text-sm">ProDoc reads a document only when asked — its figures, dates and parties, each with where it was read.</p>
          <Button variant="outline" size="sm" onClick={() => setAsking(offer.profile.name)} disabled={followed.following}>
            <ScanText size={15} aria-hidden />
            Read it as…
          </Button>
        </div>
      )}
      {offer && 'reason' in offer && <p className="text-muted-foreground text-sm">{offer.reason}</p>}

      {job && (
        <JobLine
          job={job}
          following={followed.following}
          gaveUp={followed.gaveUp}
          error={followed.error}
          types={types}
          canOpen={Boolean(onOpenExtraction && landed)}
          onOpen={() => landed && onOpenExtraction?.(doc, landed)}
          suggestion={job.suggested_profile && fitting.some((p) => p.name === job.suggested_profile) ? job.suggested_profile : null}
          onSuggestion={(name) => setAsking(name)}
          onMap={mayMap ? () => setMapping(job) : undefined}
        />
      )}
      {reaskError && <Alert tone="error">The map is saved, but {reaskError.charAt(0).toLowerCase() + reaskError.slice(1)}</Alert>}

      {records.loading && !records.data ? (
        <div className="space-y-2">
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </div>
      ) : records.error && !records.data ? (
        <Alert tone="error">
          {records.error}{' '}
          <Button variant="link" onClick={() => void records.reload()}>
            Try again
          </Button>
        </Alert>
      ) : list.length === 0 ? (
        <EmptyState variant="outline" title="Nothing read from it yet" description="When ProDoc is asked to read it, what it read — and where on the page — is listed here." />
      ) : (
        <ul className="divide-border border-border divide-y rounded-lg border" aria-label="Extractions">
          {list.map((record) => (
            <li key={record.id} className="flex flex-wrap items-center gap-3 px-3 py-3" data-extraction={record.id}>
              <div className="min-w-0 flex-1 basis-48">
                <p className="text-foreground text-sm font-medium">{documentTypeLabel(profileOf(record), types)}</p>
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                  <ExtractionStateBadge state={record.review?.state} size="sm" />
                  <ReconciledBadge reconciled={record.reconciled} size="sm" />
                  {record.stale && (
                    <Badge tone="warning" size="sm">
                      An earlier version
                    </Badge>
                  )}
                  <span className="text-muted-foreground text-xs">{record.created_at ? when(record.created_at) : ''}</span>
                </div>
                {record.review?.state === 'needs_review' && record.review.reasons.length > 0 && (
                  <p className="text-muted-foreground mt-1 text-xs">{record.review.reasons.map(reviewReasonLabel).join(' · ')}</p>
                )}
              </div>
              {onOpenExtraction && (
                <Button variant="outline" size="sm" onClick={() => onOpenExtraction(doc, record)} aria-label={`Open what was read as ${documentTypeLabel(profileOf(record), types)}`}>
                  Open
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      <ParseDialog
        client={client}
        doc={doc}
        open={asking !== null}
        initial={asking}
        fitting={fitting}
        records={list}
        types={types}
        onClose={() => setAsking(null)}
        onStarted={(answer, input) => {
          setAsking(null)
          setAsked({ ...input, password: null })
          setStarted(answer)
          setRevision((n) => n + 1)
        }}
      />

      {mapping && mapProfile && (
        <MapperDialog
          client={client}
          doc={doc}
          job={mapping}
          profile={mapProfile}
          scope={mapScope}
          types={types}
          jobPollMs={jobPollMs}
          onClose={() => setMapping(null)}
          onSaved={() => {
            const refused = mapping
            setMapping(null)
            void reask(refused)
          }}
        />
      )}
    </div>
  )
}

/**
 * W32 (FR-E55): *Map the columns* — the refused job's `mapping` (read again from the
 * job when the answer that ended it did not carry it), in `<ProDocColumnMapper>`.
 */
function MapperDialog({
  client,
  doc,
  job,
  profile,
  scope,
  types,
  jobPollMs,
  onClose,
  onSaved,
}: {
  client: ProDocClient
  doc: ProDocDocument
  job: ParseJob
  profile: ParseProfile
  scope: string
  types: DocumentTypeOption[]
  jobPollMs: number
  onClose: () => void
  onSaved: () => void
}) {
  const carried = job.mapping ?? null
  const loaded = useLoad<MappingNeeded | null>(async () => (carried ? carried : ((await client.parsing.job(job.id)).mapping ?? null)), [client, job.id, carried])
  const mapping = loaded.data
  return (
    <Dialog open onClose={onClose} title={`Map the columns of “${doc.title}”`} width="xl" sheet>
      {loaded.loading && !mapping ? (
        <div className="space-y-2" aria-busy="true">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : mapping ? (
        <ProDocColumnMapper
          client={client}
          documentId={doc.id}
          versionId={job.version_id || doc.current_version_id}
          profile={profile}
          mapping={mapping}
          scope={scope}
          types={types}
          jobPollMs={jobPollMs}
          onSaved={onSaved}
          onCancel={onClose}
        />
      ) : (
        <Alert tone="error">{loaded.error || 'ProDoc no longer has what it read of this file’s columns. Ask it to read the file again, then map it.'}</Alert>
      )}
    </Dialog>
  )
}

/** The job this tab started, as it goes: waiting, being read, read — or why not. */
function JobLine({
  job,
  following,
  gaveUp,
  error,
  types,
  canOpen,
  onOpen,
  suggestion,
  onSuggestion,
  onMap,
}: {
  job: ParseJob
  following: boolean
  gaveUp: boolean
  error: string
  types: DocumentTypeOption[]
  canOpen: boolean
  onOpen: () => void
  suggestion: string | null
  onSuggestion: (name: string) => void
  /** W32: a refusal for want of a map — open the mapper. */
  onMap?: () => void
}) {
  const as = job.profile && job.profile !== 'auto' ? documentTypeLabel(job.profile, types).toLowerCase() : 'what ProDoc finds it is'
  if (job.status === 'refused' || job.status === 'failed') {
    return (
      <Alert tone="error">
        <p className="font-medium">
          {jobStatusLabel(job.status)} — not read as {as}
        </p>
        <p className="mt-1">{jobReasonText(job.reason)} Nothing was counted against the organisation’s documents processed.</p>
        {suggestion && (
          <Button variant="link" onClick={() => onSuggestion(suggestion)}>
            It looks like {documentTypeLabel(suggestion, types).toLowerCase()} — read it as that
          </Button>
        )}
        {onMap && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={onMap} aria-describedby="prodoc-map-copy">
              Map the columns
            </Button>
            <span id="prodoc-map-copy" className="text-xs">
              {MAPPING_NEEDED_COPY}
            </span>
          </div>
        )}
      </Alert>
    )
  }
  const done = job.status === 'completed'
  const Icon = done ? CircleCheck : error ? CircleAlert : LoaderCircle
  return (
    <div className="border-border flex flex-wrap items-center gap-3 rounded-lg border px-3 py-2.5" role="status" data-job={job.status}>
      <Icon size={18} className={cn('shrink-0', done ? TONE_TEXT.success : error ? TONE_TEXT.error : 'text-primary animate-spin')} aria-hidden />
      <p className="text-foreground min-w-0 flex-1 text-sm">
        {done
          ? `Read as ${as}.`
          : error
            ? error
            : job.status === 'queued' && job.waiting === WAITING_INHOUSE
              ? waitingInhouseText(as)
              : gaveUp
                ? `Still being read as ${as}. It carries on; come back to this document later.`
                : `${jobStatusLabel(job.status)} as ${as}…${following && job.status === 'queued' ? ' A large scan takes longer.' : ''}`}
      </p>
      {done && canOpen && (
        <Button size="sm" onClick={onOpen}>
          Open it
        </Button>
      )}
    </div>
  )
}

/**
 * *Read it as…*: the profile (the document's type's, or another that reads
 * this kind of file), the password an encrypted PDF needs, and — for a version
 * already read as that — the reason it is read again (FR-E5).
 */
function ParseDialog({
  client,
  doc,
  open,
  initial,
  fitting,
  records,
  types,
  onClose,
  onStarted,
}: {
  client: ProDocClient
  doc: ProDocDocument
  open: boolean
  initial: string | null
  fitting: ParseProfile[]
  records: ExtractionRecord[]
  types: DocumentTypeOption[]
  onClose: () => void
  onStarted: (job: ParseJob, input: ParseInput) => void
}) {
  const [profile, setProfile] = useState(initial ?? '')
  const [password, setPassword] = useState('')
  const [reason, setReason] = useState('')
  const [gstin, setGstin] = useState('')
  const [pan, setPan] = useState('')
  const [tried, setTried] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    // Each opening starts clean — the password above all is never kept between requests;
    // whose books is the person's to say for each reading, as ProDoc keeps no record of it.
    if (open) {
      setProfile(initial ?? fitting[0]?.name ?? '')
      setPassword('')
      setReason('')
      setGstin('')
      setPan('')
      setTried(false)
      setError('')
    } else setPassword('')
    // Only the opening is the question.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  if (!open) return null
  const label = documentTypeLabel(profile, types)
  const earlier = records.find((r) => r.version_id === doc.current_version_id && profileOf(r) === profile)
  // W30 (FR-67): a saved password is used by the service when none is sent — so nothing is asked here.
  const savedPassword = doc.encrypted && doc.media_type === 'application/pdf' && passwordSavedOf(doc)
  const needsPassword = doc.encrypted && doc.media_type === 'application/pdf' && !savedPassword
  const reasonTooLong = reason.trim().length > MAX_CORRECTION_REASON
  // T15.16: the shapes are checked here; whether a GSTIN's check digit holds is the engine's to say.
  const party = partyContextOf(gstin, pan)
  const partyErrors = tried && 'errors' in party ? party.errors : {}

  const submit = async () => {
    if (busy || !profile || reasonTooLong) return
    setTried(true)
    if ('errors' in party) return
    if (earlier && !reason.trim()) {
      setError('Say why it is read again — the earlier reading stays, and this one is counted as another document processed.')
      return
    }
    setBusy(true)
    setError('')
    try {
      const input: ParseInput = {
        profile,
        version_id: doc.current_version_id,
        review: 'auto',
        context: party.context,
        password: needsPassword && password ? password : null,
        force: Boolean(earlier),
        reason: earlier ? reason.trim() : null,
      }
      const answer = await client.parsing.parse(doc.id, input)
      setPassword('')
      // The password goes with this one request and is handed on to nothing.
      onStarted(answer.job, { ...input, password: null })
    } catch (e) {
      setError(errorText(e, 'ProDoc was not asked to read it.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onClose={onClose} title={`Read “${doc.title}”`} error={error || null}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault()
          void submit()
        }}
      >
        {fitting.length > 1 ? (
          <RadioGroup legend="Read it as" options={fitting.map((p) => ({ value: p.name, label: documentTypeLabel(p.name, types) }))} value={profile} onChange={setProfile} />
        ) : (
          <p className="text-foreground text-sm">
            Read it as <span className="font-medium">{label.toLowerCase()}</span> — its figures, dates and parties, each with where on the page it was read, and every check
            that should add up.
          </p>
        )}
        {needsPassword && (
          <Input
            type="password"
            label="The PDF’s password"
            autoComplete="off"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            hint="It is encrypted. The password opens it once for this reading and is kept nowhere."
          />
        )}
        {savedPassword && <p className="text-muted-foreground text-xs">It is encrypted; ProDoc reads it with its saved password, which nobody is shown.</p>}
        <fieldset className="space-y-3">
          <legend className="text-foreground mb-1 text-sm font-medium">Whose books (optional)</legend>
          <p className="text-muted-foreground text-xs">
            Without the party’s GSTIN, an invoice’s side — sale or purchase — is unknown, and it goes to a reviewer.
          </p>
          <Input
            id="prodoc-parse-gstin"
            label="GSTIN"
            value={gstin}
            autoComplete="off"
            onChange={(e) => setGstin(e.target.value)}
            hint="The client’s or the company’s — 15 characters. Several, separated by commas."
            error={partyErrors.gstin ?? null}
          />
          <Input
            id="prodoc-parse-pan"
            label="PAN"
            value={pan}
            autoComplete="off"
            onChange={(e) => setPan(e.target.value)}
            hint="10 characters — whose document it should be, checked against what it names."
            error={partyErrors.pan ?? null}
          />
        </fieldset>
        {earlier && (
          <Input
            id="prodoc-parse-reason"
            label="Why read it again"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            hint={`This version was read as ${label.toLowerCase()} ${earlier.created_at ? when(earlier.created_at) : 'before'}; that reading stays. Reading it again counts as another document processed.`}
            error={reasonTooLong ? `Up to ${MAX_CORRECTION_REASON} characters.` : null}
          />
        )}
        <p className="text-muted-foreground text-xs">
          Reading counts as one document processed for the organisation once it is read; a refusal is not counted. What is read is checked by a reviewer unless it adds up
          and the organisation accepts it automatically.
        </p>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" loading={busy} loadingText="Asking…" disabled={!profile || reasonTooLong}>
            {earlier ? 'Read it again' : 'Read it'}
          </Button>
        </div>
      </form>
    </Dialog>
  )
}
